/**
 * Learn — in-app help center. Lists every enabled feature (only those
 * the caller can actually access) grouped by category. Click a card to
 * open a right-side drawer with the marketing hook + how-to blurb.
 * Admins get a Regenerate + inline edit affordance.
 *
 * Content strategy: AI-drafted the first time a feature is opened, cached
 * server-side in `feature_help`. Admin edits win. See routers/learn.py.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search, Sparkles, X, Save, Pencil } from "lucide-react";

const CATEGORY_ORDER = ["nav", "page-section", "action", "integration"];
const CATEGORY_LABEL = {
    "nav": "Pages you can visit",
    "page-section": "Sections on those pages",
    "action": "Actions you can take",
    "integration": "Connected integrations",
};

export default function Learn() {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "admin" || user?.role === "employer";
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(null); // active feature_id

    useEffect(() => {
        setLoading(true);
        api.get("/learn")
            .then(({ data }) => setTopics(data.topics || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return topics;
        return topics.filter((t) =>
            t.label.toLowerCase().includes(s) ||
            t.description.toLowerCase().includes(s) ||
            t.feature_id.toLowerCase().includes(s)
        );
    }, [topics, q]);

    const grouped = useMemo(() => {
        const g = {};
        for (const t of filtered) (g[t.category] ||= []).push(t);
        return g;
    }, [filtered]);

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="learn-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Help center</div>
                        <h1 className="font-display font-black text-3xl text-ikb leading-tight">Learn what you have</h1>
                        <p className="text-xs text-muted-foreground mt-1">
                            A friendly walk-through of every feature enabled for your workspace. Tap a card to see the pitch and the how-to.
                        </p>
                    </div>
                    <label className="relative">
                        <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search features…"
                            className="h-9 border border-border bg-white text-sm pl-8 pr-3 min-w-[240px]"
                            data-testid="learn-search"
                        />
                    </label>
                </div>
            </header>

            {loading ? (
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
                CATEGORY_ORDER.map((cat) => {
                    const list = grouped[cat] || [];
                    if (!list.length) return null;
                    return (
                        <section key={cat} className="mb-6">
                            <h2 className="text-[10px] font-mono uppercase tracking-widest text-ikb mb-2">{CATEGORY_LABEL[cat] || cat} · {list.length}</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {list.map((t) => (
                                    <button
                                        key={t.feature_id}
                                        type="button"
                                        onClick={() => setOpen(t.feature_id)}
                                        className="text-left border border-border bg-white hover:border-ikb hover:shadow-sm p-3 transition-all"
                                        data-testid={`learn-card-${t.feature_id}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Sparkles className="w-3.5 h-3.5 text-ikb" />
                                            <div className="font-semibold text-sm truncate">{t.label}</div>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    );
                })
            )}

            {open && (
                <LearnDrawer
                    featureId={open}
                    isAdmin={isAdmin}
                    onClose={() => setOpen(null)}
                    onRefreshList={() => api.get("/learn").then(({ data }) => setTopics(data.topics || []))}
                />
            )}
        </div>
    );
}

/** Right-side drawer that lazily fetches the blurb (AI draft on first hit). */
function LearnDrawer({ featureId, isAdmin, onClose, onRefreshList }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [regen, setRegen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [marketing, setMarketing] = useState("");
    const [howTo, setHowTo] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/learn/${featureId}`)
            .then(({ data }) => {
                setData(data);
                setMarketing(data.marketing || "");
                setHowTo(data.how_to || "");
            })
            .catch((e) => { toast.error(formatApiError(e)); onClose(); })
            .finally(() => setLoading(false));
    }, [featureId, onClose]);

    async function regenerate() {
        setRegen(true);
        try {
            const { data } = await api.post(`/learn/${featureId}/regenerate`);
            setData((d) => ({ ...d, ...data }));
            setMarketing(data.marketing || ""); setHowTo(data.how_to || "");
            toast.success("Regenerated");
            onRefreshList?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRegen(false); }
    }
    async function save() {
        setSaving(true);
        try {
            const { data } = await api.patch(`/learn/${featureId}`, { marketing, how_to: howTo });
            setData((d) => ({ ...d, ...data }));
            setEditing(false);
            toast.success("Saved");
            onRefreshList?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
            onClick={onClose}
            data-testid="learn-drawer"
        >
            <div
                className="w-full sm:max-w-lg bg-white h-full overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-start gap-2 z-10">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{data?.feature?.category}</div>
                        <div className="font-display font-black text-lg leading-tight text-ikb">{data?.feature?.label || "…"}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 hover:bg-neutral-100"
                        data-testid="learn-drawer-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Drafting help content…
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {editing ? (
                            <>
                                <label className="block">
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Marketing hook</span>
                                    <textarea
                                        value={marketing}
                                        onChange={(e) => setMarketing(e.target.value)}
                                        rows={4}
                                        className="mt-1 w-full border border-border bg-white text-sm px-2 py-1.5 resize-none"
                                        data-testid="learn-edit-marketing"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">How to use</span>
                                    <textarea
                                        value={howTo}
                                        onChange={(e) => setHowTo(e.target.value)}
                                        rows={5}
                                        className="mt-1 w-full border border-border bg-white text-sm px-2 py-1.5 resize-none"
                                        data-testid="learn-edit-howto"
                                    />
                                </label>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={save}
                                        disabled={saving}
                                        className="h-8 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5 disabled:opacity-60"
                                        data-testid="learn-edit-save"
                                    >
                                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setEditing(false); setMarketing(data?.marketing || ""); setHowTo(data?.how_to || ""); }}
                                        className="h-8 px-3 border border-border bg-white text-[11px] font-mono uppercase tracking-widest"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <section className="border border-ikb/20 bg-ikb/5 p-3">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb mb-1">Why it&apos;s great</div>
                                    <p className="text-sm text-foreground/90 whitespace-pre-wrap" data-testid="learn-marketing">
                                        {marketing || "—"}
                                    </p>
                                </section>
                                <section>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">How to use it</div>
                                    <p className="text-sm text-foreground/80 whitespace-pre-wrap" data-testid="learn-howto">
                                        {howTo || "—"}
                                    </p>
                                </section>
                                {isAdmin && (
                                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                                        <button
                                            type="button"
                                            onClick={() => setEditing(true)}
                                            className="h-8 px-3 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-neutral-50"
                                            data-testid="learn-edit-open"
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={regenerate}
                                            disabled={regen}
                                            className="h-8 px-3 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-neutral-50 disabled:opacity-60"
                                            data-testid="learn-regenerate"
                                        >
                                            {regen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                            Regenerate with AI
                                        </button>
                                        <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                                            {data?.generated_by === "ai" ? "AI-drafted" : "Human-edited"}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
